/* eslint-disable react-hooks/exhaustive-deps */
import { createStyles, FormControl, FormControlLabel, FormLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartEmptyState } from '../ChartEmptyState';
import AccessTimeOutlinedIcon from '@material-ui/icons/AccessTimeOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        formControl: {
            display: 'flex',
            flexFlow: 'row wrap',
            alignItems: 'center',
            justifyContent: 'center',
        },
        formLabel: {
            marginRight: '10px',
        },
        scrollUp: {
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            position: 'absolute',
            bottom: '90px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        }
    }),
);

export enum MatterOptionEnum {
    ALL_MATTER = 0,
    CURRENT_MATTER,
    CLOSE_MATTER,
}

export enum GroupOptionEnum {
    MATTER_CATEGORY = 0,
    MATTER_TYPE,
}

interface AverageMatterDurationChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    selectedOption?: MatterOptionEnum;
    additionalOption?: GroupOptionEnum;
}

interface ChartState {
    option: GroupOptionEnum;
    matterOption: MatterOptionEnum;
    averageDuration: number;
    groupedBy: string[];
    avgDays: number[];
    count: number[];
}

export const AverageMatterDurationChart: React.FC<AverageMatterDurationChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollUpAndPrevRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollDownAndNextRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const matterDurationByCategoryQuery = useQuery<MatterDurationByCategoryData>(MATTER_DURATION_BY_CATEGORY_QYERY, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
            showAllRecords: props.showAllRecords
        },
        fetchPolicy: 'network-only',
    });

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0
    })[1];

    useEffect(() => {
        window.addEventListener('keydown', onKeyDownHandler);

        return () => {
            window.removeEventListener('keydown', onKeyDownHandler);
        };
        // tslint:disable-next-line: align
    }, []);

    // tslint:disable-next-line: no-any
    const onKeyDownHandler = (event: any) => {
        if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey) {
            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            } else {
                event.stopPropagation();
            }
            return;
        }
    };

    const [state, setState] = useState<ChartState>({
        option: props.additionalOption === undefined ? GroupOptionEnum.MATTER_CATEGORY : props.additionalOption,
        matterOption: props.selectedOption === undefined ? MatterOptionEnum.CLOSE_MATTER : props.selectedOption,
        averageDuration: 0,
        groupedBy: [],
        avgDays: [],
        count: [],
    });

    useEffect(() => {
        // tslint:disable-next-line: max-line-length
        if (
            matterDurationByCategoryQuery.data &&
            matterDurationByCategoryQuery.data.statistics &&
            matterDurationByCategoryQuery.data.statistics.matter &&
            !matterDurationByCategoryQuery.loading &&
            !matterDurationByCategoryQuery.error
        ) {
            setState((prevState) => {
                let averageDuration = 0;
                let groupedBy: string[] = [];
                let avgDays: number[] = [];
                let count: number[] = [];

                if (state.option === GroupOptionEnum.MATTER_CATEGORY) {
                    if (state.matterOption === MatterOptionEnum.ALL_MATTER) {
                        averageDuration = matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterCategoryWithAllMatters.averageDuration;
                        // tslint:disable-next-line: max-line-length
                        const averageMatterDuration =
                            matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterCategoryWithAllMatters.averageMatterDuration;

                        groupedBy = averageMatterDuration.map((matter: AverageMatterDuration) => matter.groupedBy);
                        avgDays = averageMatterDuration.map((matter: AverageMatterDuration) => matter.avgDays);
                        count = averageMatterDuration.map((matter: AverageMatterDuration) => matter.count);
                    } else if (state.matterOption === MatterOptionEnum.CURRENT_MATTER) {
                        // tslint:disable-next-line: max-line-length
                        averageDuration =
                            matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterCategoryWithCurrentMatters.averageDuration;

                        // tslint:disable-next-line: max-line-length
                        const averageMatterDuration =
                            matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterCategoryWithCurrentMatters.averageMatterDuration;

                        groupedBy = averageMatterDuration.map((matter: AverageMatterDuration) => matter.groupedBy);
                        avgDays = averageMatterDuration.map((matter: AverageMatterDuration) => matter.avgDays);
                        count = averageMatterDuration.map((matter: AverageMatterDuration) => matter.count);
                    } else {
                        averageDuration = matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterCategoryWithClosedMatters.averageDuration;

                        // tslint:disable-next-line: max-line-length
                        const averageMatterDuration =
                            matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterCategoryWithClosedMatters.averageMatterDuration;

                        groupedBy = averageMatterDuration.map((matter: AverageMatterDuration) => matter.groupedBy);
                        avgDays = averageMatterDuration.map((matter: AverageMatterDuration) => matter.avgDays);
                        count = averageMatterDuration.map((matter: AverageMatterDuration) => matter.count);
                    }
                } else {
                    if (state.matterOption === MatterOptionEnum.ALL_MATTER) {
                        averageDuration = matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterTypeWithAllMatters.averageDuration;
                        // tslint:disable-next-line: max-line-length
                        const averageMatterDuration =
                            matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterTypeWithAllMatters.averageMatterDuration;

                        groupedBy = averageMatterDuration.map((matter: AverageMatterDuration) => matter.groupedBy);
                        avgDays = averageMatterDuration.map((matter: AverageMatterDuration) => matter.avgDays);
                        count = averageMatterDuration.map((matter: AverageMatterDuration) => matter.count);
                    } else if (state.matterOption === MatterOptionEnum.CURRENT_MATTER) {
                        // tslint:disable-next-line: max-line-length
                        averageDuration = matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterTypeWithCurrentMatters.averageDuration;
                        // tslint:disable-next-line: max-line-length
                        const averageMatterDuration =
                            matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterTypeWithCurrentMatters.averageMatterDuration;

                        groupedBy = averageMatterDuration.map((matter: AverageMatterDuration) => matter.groupedBy);
                        avgDays = averageMatterDuration.map((matter: AverageMatterDuration) => matter.avgDays);
                        count = averageMatterDuration.map((matter: AverageMatterDuration) => matter.count);
                    } else {
                        averageDuration = matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterTypeWithClosedMatters.averageDuration;
                        // tslint:disable-next-line: max-line-length
                        const averageMatterDuration =
                            matterDurationByCategoryQuery.data!.statistics.matter.matterDurationsByCategoryOrTypeType.matterTypeWithClosedMatters.averageMatterDuration;

                        groupedBy = averageMatterDuration.map((matter: AverageMatterDuration) => matter.groupedBy);
                        avgDays = averageMatterDuration.map((matter: AverageMatterDuration) => matter.avgDays);
                        count = averageMatterDuration.map((matter: AverageMatterDuration) => matter.count);
                    }
                }

                return {
                    ...prevState,
                    averageDuration,
                    groupedBy,
                    avgDays,
                    count,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.option, state.matterOption, matterDurationByCategoryQuery.loading, matterDurationByCategoryQuery.error, matterDurationByCategoryQuery.data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isMatterDurationByCategoryOrTypeChartOpen');
        }
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize(state.matterOption, state.option);
        }
    };

    const onScrollUpAndPrev = (type: 'up' | 'prev') => {
        if (chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min > 0) {
            const scrollRatio = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            const min = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            if (scrollRatio < chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min) {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= scrollRatio ? scrollRatio : 1;
            } else {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= min;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= min;
            }
            chartRef.current.options.scales[type === 'up' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll up
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll up
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }
        scrollDownAndNextRef.current.classList.remove('Mui-disabled');
    };

    const onScrollDownAndNext = (type: 'down' | 'next') => {

        const labels = state.groupedBy;

        if (chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max < labels.length) {
            const remainingBar = labels.length - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max;
            const scrollRatio = chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min;
            if (remainingBar <= scrollRatio) {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += remainingBar;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += remainingBar;
            } else {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += scrollRatio ? scrollRatio : 1;
            }
            chartRef.current.options.scales[type === 'down' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll down
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll down
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
        scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
    };

    const onScollBar = (type: 'up' | 'down' | 'prev' | 'next') => () => {
        if (type === 'up') {
            onScrollUpAndPrev('up');
        } else if (type === 'down') {
            onScrollDownAndNext('down');
        } else if (type === 'next') {
            onScrollDownAndNext('next');
        } else {
            onScrollUpAndPrev('prev');
        }
    };

    const onDisabledEnabledArrow = (barType: 'horizontal' | 'vertical') => {
        const labels = state.groupedBy;
        // up 
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].min > 0) {
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }

        // down
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].max < labels.length) {
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
    };

    const onPan = (context: { chart: Chart }) => {

        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const onZoom = (context: { chart: Chart }) => {

        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    // tslint:disable-next-line: no-any
    const onContextMenu = (event: any) => {
        event.preventDefault();
        if (event.button !== 2) {
            return false;
        }
        return true;
    };

    // tslint:disable-next-line: no-any
    const onDownload = (name: string, extension: string = 'jpg') => (image: any) => {
        const a = document.createElement('a');
        a.href = image;
        a.download = createFileName(extension, name);
        a.click();
    };

    const onDownloadScreenshot = () => {
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload('Average Matter Duration'));
            // tslint:disable-next-line: align
        }, 500);
    };

    const chartControllerComponent = () => {
        return (
            <>
                <IconButtonWrapper aria-label="close">
                    <ChartInfo 
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        className={`${classes.helpIcon} rv-chart-help`}
                    />
                    {props.showAllRecords && (
                        <IconButton size="medium" onClick={onDownloadScreenshot}>
                            <GetAppOutlinedIcon color="primary" fontSize="large" />
                        </IconButton>
                    )}
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon className={classes.closeIcon} fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    const handleTotalClick = () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            const tempPropertyType = state.option === GroupOptionEnum.MATTER_CATEGORY ? 'Matter Category' : 'Matter Type';

            searchCriterias.push(
                {
                    property: 'InstructionDate',
                    operator: 'isnot',
                    comparison: 'null',
                })
            ;

            if (state.matterOption === MatterOptionEnum.CURRENT_MATTER) {
                // current matters
                searchCriterias.push({
                    property: 'InProgress',
                    operator: 'eq',
                    comparison: '1',
                });
            } else if (state.matterOption === MatterOptionEnum.CLOSE_MATTER) {
                // closed matters
                searchCriterias.push({
                    property: 'InProgress',
                    operator: 'eq',
                    comparison: '0',
                });
            }

            props.getSelectedItem(searchCriterias, true, `Average Duration by ${tempPropertyType}`, DrillDownType.matterSummaryDrilldown, 'Duration');
        }
    };

    const onChangeRadio = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                [name]: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };
    
    if (matterDurationByCategoryQuery.loading) {
        return (
            <ChartSkeleton variant="bars" />
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.avgDays) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
            {chartControllerComponent()}
            <div className={classes.titleContainer}>
                <ChartHeading icon={<AccessTimeOutlinedIcon />} title="Average Duration" />
                <div className={`${classes.total} h2`} onClick={handleTotalClick}>
                    <AnimatedNumber value={state.averageDuration} format={(amount) => formatNumber(amount)} />
                </div>
                <div className={classes.title}>Days</div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                {props.showAllRecords && (
                    <>
                        <div className={`${classes.scrollUp} scroll`}>
                            <IconButton 
                                size="medium" 
                                onClick={onScollBar('up')}
                                ref={scrollUpAndPrevRef}
                            >
                                <KeyboardArrowUpIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                        <div className={`${classes.scrollDown} scroll`}>
                            <IconButton 
                                size="medium" 
                                onClick={onScollBar('down')}
                                ref={scrollDownAndNextRef}
                            >
                                <KeyboardArrowDownIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                    </>
                )}
                <Bar
                    ref={chartRef}
                    onContextMenu={onContextMenu}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.groupedBy[_index];

                            const searchCriterias = new Array<SearchCriteria>();

                            const tempPropertyType = state.option === GroupOptionEnum.MATTER_CATEGORY ? 'Matter Category' : 'Matter Type';

                            searchCriterias.push(
                                {
                                    property: state.option === GroupOptionEnum.MATTER_CATEGORY ? 'MatterCategory' : 'MatterType',
                                    operator: 'eq',
                                    comparison: item,
                                },
                                {
                                    property: 'InstructionDate',
                                    operator: 'isnot',
                                    comparison: 'null',
                                }
                            );

                            props.getSelectedItem(
                                searchCriterias, 
                                true, 
                                `Average Duration by ${tempPropertyType}`, 
                                DrillDownType.matterSummaryDrilldown, 
                                'Duration'
                            );
                        }
                    }}
                    data={{
                        labels: getLabels(state.groupedBy),
                        datasets: [
                            {
                                label: 'Average days',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary, true),
                                data: state.avgDays,
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                stacked: true,
                                suggestedMax: getSuggestedMaxTick(state.avgDays),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.avgDays),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                stacked: true,
                                max: props.showAllRecords ? 29 : 9,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: props.showAllRecords,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: props.showAllRecords,
                                    },
                                    onZoom: onZoom,
                                },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return [`${dataset.label}: ${formatNumber(currentValue)}`];
                                    },
                                    // tslint:disable-next-line: no-any no-shadowed-variable
                                    afterLabel: function (context: any) {

                                        const {
                                              dataIndex,
                                        } = context;

                                        const count = state.count[dataIndex];

                                        return [
                                            `Matters: ${formatNumber(count)}`,
                                        ];
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.avgDays) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div" className={classes.formControl}>
                <FormLabel component="legend" className={classes.formLabel}>
                    Group By
                </FormLabel>
                <RadioGroup name="option" value={state.option} onChange={onChangeRadio('option')} className={classes.radioGroup}>
                    <FormControlLabel value={GroupOptionEnum.MATTER_CATEGORY} control={<Radio color="primary" />} label="Matter Category" />
                    <FormControlLabel value={GroupOptionEnum.MATTER_TYPE} control={<Radio color="primary" />} label="Matter Type" />
                </RadioGroup>
            </FormControl>
            <FormControl component="div" className={classes.formControl}>
                <FormLabel component="legend" className={classes.formLabel}>
                    Include
                </FormLabel>
                <RadioGroup name="matterOption" value={state.matterOption} onChange={onChangeRadio('matterOption')} className={classes.radioGroup}>
                    <FormControlLabel value={MatterOptionEnum.CLOSE_MATTER} control={<Radio color="primary" />} label="Closed Matters" />
                    <FormControlLabel value={MatterOptionEnum.CURRENT_MATTER} control={<Radio color="primary" />} label="Current Matters" />
                    <FormControlLabel value={MatterOptionEnum.ALL_MATTER} control={<Radio color="primary" />} label="All Matters" />
                </RadioGroup>
            </FormControl>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface MatterDurationByCategoryData {
    statistics: Statistics;
}

export interface Statistics {
    matter: Matter;
}

export interface Matter {
    matterDurationsByCategoryOrTypeType: MatterDurationsByCategoryOrTypeType;
}

export interface MatterDurationsByCategoryOrTypeType {
    matterCategoryWithAllMatters: MatterMatters;
    matterCategoryWithClosedMatters: MatterMatters;
    matterCategoryWithCurrentMatters: MatterMatters;
    matterTypeWithAllMatters: MatterMatters;
    matterTypeWithCurrentMatters: MatterMatters;
    matterTypeWithClosedMatters: MatterMatters;
}

export interface MatterMatters {
    averageDuration: number;
    averageMatterDuration: AverageMatterDuration[];
}

export interface AverageMatterDuration {
    groupedBy: string;
    avgDays: number;
    count: number;
}

const MATTER_DURATION_BY_CATEGORY_QYERY = gql`
    query MatterDurationByCategory($entityType: Int, $entityGuid: String, $responsibility: Int, $showAllRecords: Boolean) {
        statistics {
            matter {
                matterDurationsByCategoryOrTypeType(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility, showAllRecords: $showAllRecords) {
                    matterCategoryWithAllMatters {
                        averageDuration
                        averageMatterDuration {
                            groupedBy
                            avgDays
                            count
                        }
                    }
                    matterCategoryWithClosedMatters {
                        averageDuration
                        averageMatterDuration {
                            groupedBy
                            avgDays
                            count
                        }
                    }
                    matterCategoryWithCurrentMatters {
                        averageDuration
                        averageMatterDuration {
                            groupedBy
                            avgDays
                            count
                        }
                    }
                    matterTypeWithAllMatters {
                        averageDuration
                        averageMatterDuration {
                            groupedBy
                            avgDays
                            count
                        }
                    }
                    matterTypeWithCurrentMatters {
                        averageDuration
                        averageMatterDuration {
                            groupedBy
                            avgDays
                            count
                        }
                    }
                    matterTypeWithClosedMatters {
                        averageDuration
                        averageMatterDuration {
                            groupedBy
                            avgDays
                            count
                        }
                    }
                }
            }
        }
    }
`;
